/**
 * Material Category Service
 * Handles API requests related to material category management
 */
import apiClient from './apiClient';

// Define the base URL for material category service
const baseUrl = '/api/supplier/material-category';

const materialCategoryService = {
  /**
   * Get all material categories
   * @param {boolean} includeInactive - Whether to include inactive categories
   * @returns {Promise} - Response with category list
   */
  getAllCategories(includeInactive = false) {
    return apiClient.get(baseUrl, {
      params: { includeInactive }
    }).then(response => response.data);
  },

  /**
   * Get a material category by ID
   * @param {string} id - Category ID
   * @returns {Promise} - Response with category details
   */
  getCategoryById(id) {
    if (!id) {
      console.error('getCategoryById: id is required');
      return Promise.reject(new Error('Category ID is required'));
    }
    
    return apiClient.get(`${baseUrl}/${id}`)
      .then(response => response.data);
  },

  /**
   * Create a new material category
   * @param {Object} category - Category data
   * @returns {Promise} - Response with created category
   */
  createCategory(category) {
    if (!category) {
      console.error('createCategory: category data is required');
      return Promise.reject(new Error('Category data is required'));
    }
    
    return apiClient.post(baseUrl, category)
      .then(response => response.data);
  },

  /**
   * Update an existing material category
   * @param {string} id - Category ID
   * @param {Object} category - Updated category data
   * @returns {Promise} - Response with updated category
   */
  updateCategory(id, category) {
    if (!id) {
      console.error('updateCategory: id is required');
      return Promise.reject(new Error('Category ID is required'));
    }
    
    if (!category) {
      console.error('updateCategory: category data is required');
      return Promise.reject(new Error('Category data is required'));
    }
    
    return apiClient.put(`${baseUrl}/${id}`, category)
      .then(response => response.data);
  },

  /**
   * Search for material categories
   * @param {Object} searchParams - Search parameters
   * @returns {Promise} - Response with search results
   */
  searchCategories(searchParams = {}) {
    return apiClient.post(`${baseUrl}/search`, searchParams)
      .then(response => response.data);
  },

  /**
   * Get child categories for a parent category
   * @param {string} parentId - Parent category ID
   * @returns {Promise} - Response with child categories
   */
  getChildCategories(parentId) {
    if (!parentId) {
      console.error('getChildCategories: parentId is required');
      return Promise.reject(new Error('Parent ID is required'));
    }
    
    return apiClient.get(`${baseUrl}/children/${parentId}`)
      .then(response => response.data);
  },

  /**
   * Check if a category code is unique
   * @param {string} id - Category ID for edit mode or tenant ID for create mode
   * @param {string} code - Category code to check
   * @returns {Promise} - Response with uniqueness result
   */
  isCodeUnique(id, code) {
    if (!id) {
      console.error('isCodeUnique: id is required');
      return Promise.reject(new Error('ID is required'));
    }
    
    if (!code) {
      console.error('isCodeUnique: code is required');
      return Promise.reject(new Error('Category code is required'));
    }
    
    return apiClient.post(`${baseUrl}/${id}/is-code-unique`, null, {
      params: { code }
    }).then(response => response.data);
  },

  /**
   * Delete a material category by ID
   * @param {string} id - Category ID to delete
   * @returns {Promise} - Response from the delete operation
   */
  deleteCategory(id) {
    if (!id) {
      console.error('deleteCategory: id is required');
      return Promise.reject(new Error('Category ID is required for deletion'));
    }
    
    return apiClient.delete(`${baseUrl}/${id}`)
      .then(response => response.data); // Or handle based on API response (e.g., status code)
  }
};

export default materialCategoryService; 